import { useCallback, useEffect, useRef, useState } from 'react';

import { RefreshContentSearch } from 'data/generated/graphql';
import { useAllessehContentUpdateContext } from './AllessehContentUpdateContext';

interface UseAllessehContentUpdateEventOptions {
  enabled?: boolean;
  debounceMs?: number;
  delayMs?: number;
}

/**
 * Returns the most recent raw subscription event from the backend, or null if none has arrived.
 * Fires for every update regardless of originId. Use this to trigger broad refreshes
 * such as re-fetching a search results list.
 *
 * Options:
 * - enabled: set false to skip tracking (default true)
 * - debounceMs: batches rapid events — resets the timer on each new event (default 0)
 * - delayMs: after the debounce settles, waits this long before surfacing the event.
 *   Once started, this timer is never cancelled — each debounce batch gets its own
 *   independent delay and they run concurrently (default 0)
 */
export const useAllessehContentUpdateEvent = ({
  enabled = true,
  debounceMs = 0,
  delayMs = 0
}: UseAllessehContentUpdateEventOptions = {}): RefreshContentSearch | null => {
  const { subscribeToEvents, unsubscribeFromEvents } = useAllessehContentUpdateContext();
  const [lastEvent, setLastEvent] = useState<RefreshContentSearch | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleEvent = useCallback(
    (event: RefreshContentSearch) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        setTimeout(() => {
          setLastEvent(event);
        }, delayMs);
      }, debounceMs);
    },
    [debounceMs, delayMs]
  );

  useEffect(() => {
    if (!enabled) return () => {};
    subscribeToEvents(handleEvent);
    return () => {
      unsubscribeFromEvents(handleEvent);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enabled, handleEvent, subscribeToEvents, unsubscribeFromEvents]);

  return lastEvent;
};
